/**
 * @file: matchDeliveryCheckTemplate.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import type { DeliveryCheckTemplate } from '@euler/model/DeliveryCheckTemplateInfo';
import { VehicleFuelType } from '@euler/model/enum';
import { DeliveryCheckTemplateMatchType } from '@euler/model/enum/DeliveryCheckTemplateMatchType';
import { parseTagsFromString } from '@euler/utils/orderTags';
import { uniqBy } from 'ramda';

export type DeliveryCheckTemplateMatchOptions = {
  agentId?: number;
  orgId: number;
  storeId: number;
  hasInspection: boolean;
  hasConstruction: boolean;
  fuelType?: VehicleFuelType;
  tags?: string;
};

type Predict = (x: DeliveryCheckTemplate) => boolean;

/** Represents a match tuple and related information for a given template */
type Match = [
  /* 0: inspection */ number,
  /* 1: construction*/ number,
  /* 2: order type */ string,
  /* 3: fuel type */ number,
  /* 4: cardinality */ number,
  /* 5: the template matched */ DeliveryCheckTemplate,
];

/**
 * Represents a group of matches each of which will have the same match types.
 * For example, two matches in a group both have inspection & construction
 * match types.
 */
type MatchGroup = {
  matches: Match[];
};

/** Represents match information about matching against a specific property */
type PropertyMatch = { matched: boolean; specified: boolean };

type BoolMatchers = {
  [p in DeliveryCheckTemplateMatchType]: (value: boolean) => PropertyMatch;
};

const toPropMatch = (x: [boolean, boolean]) => ({
  matched: x[0],
  specified: x[1],
});

const boolMatchers: BoolMatchers = {
  [DeliveryCheckTemplateMatchType.None]: () => toPropMatch([true, false]),
  [DeliveryCheckTemplateMatchType.Yes]: x => toPropMatch([x, true]),
  [DeliveryCheckTemplateMatchType.No]: x => toPropMatch([!x, true]),
};

const matchBool = (value: boolean, target: DeliveryCheckTemplateMatchType) => {
  return boolMatchers[target](value);
};

const matchTag = (tagSet: Set<string>, target: string) =>
  toPropMatch([target ? tagSet.has(target) : true, Boolean(target)]);

const matchFuelType = (
  fuelType: VehicleFuelType | null | undefined,
  target: VehicleFuelType | null | undefined,
) => toPropMatch([target == null || fuelType === target, target != null]);

//#region match utility functions

const MATCH_INSPECTION = 0;
const MATCH_CONSTRUCTION = 1;
const MATCH_ORDER_TYPE = 2;
const MATCH_FUEL_TYPE = 3;
const MATCH_CARDINALITY = 4;
const MATCH_TEMPLATE = 5;

const cardinalityOfMatch = (match: Match): number => match[MATCH_CARDINALITY];

const cardinalityOfMatchGroup = (group: MatchGroup): number =>
  cardinalityOfMatch(group.matches[0]);

const templateOfMatch = (match: Match): DeliveryCheckTemplate =>
  match[MATCH_TEMPLATE];

// determines if the given match a is a sub match of given match b
const isSubMatchOf = (a: Match, b: Match) => {
  const [x1, y1, z1, f1] = a;
  const [x2, y2, z2, f2] = b;
  return (
    x1 >= x2 && y1 >= y2 && f1 >= f2 && (z1 === z2 || (z1 !== '' && z2 === ''))
  );
};

// determines if two matches have the exactly same match types
const matchEquals = (a: Match, b: Match) =>
  a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

//#endregion

/**
 * Find a best match from the given list of delivery check templates according
 * to the given user and order/task information.
 * @param templates a list of templates to find best match from
 * @param options specify user and order/task information
 * @returns best match (or combined best match) if possible otherwise undefined
 */
export const matchDeliveryCheckTemplate = (
  templateList: DeliveryCheckTemplate[],
  options: DeliveryCheckTemplateMatchOptions,
): DeliveryCheckTemplate | undefined => {
  const {
    agentId,
    orgId,
    storeId,
    hasInspection,
    hasConstruction,
    tags,
    fuelType,
  } = options;
  const tagSet = new Set(parseTagsFromString(tags ?? ''));

  // function that returns a match structure with match details if the
  // given template matches the criterions otherwise returns null
  const matchTemplate = (template: DeliveryCheckTemplate): Match | null => {
    const inspectionMatch = matchBool(
      hasInspection,
      template.withInspectionResults,
    );
    const constructionMatch = matchBool(
      hasConstruction,
      template.withConstructionJobs,
    );
    const orderTypeMatch = matchTag(tagSet, template.withOrderType);
    const fuelTypeMatch = matchFuelType(fuelType, template.fuelType);

    if (
      inspectionMatch.matched &&
      constructionMatch.matched &&
      orderTypeMatch.matched &&
      fuelTypeMatch.matched
    ) {
      const match: Match = [0, 0, '', 0, 0, template];

      if (inspectionMatch.specified) {
        match[MATCH_INSPECTION] = 1;
        match[MATCH_CARDINALITY]++;
      }
      if (constructionMatch.specified) {
        match[MATCH_CONSTRUCTION] = 1;
        match[MATCH_CARDINALITY]++;
      }
      if (orderTypeMatch.specified) {
        match[MATCH_ORDER_TYPE] = template.withOrderType;
        match[MATCH_CARDINALITY]++;
      }
      if (fuelTypeMatch.specified) {
        match[MATCH_FUEL_TYPE] = 1;
        match[MATCH_CARDINALITY] += 10;
      }

      return match;
    }

    return null;
  };

  // perform match in given scoped specified by the given predict/filter
  const matchInScope = (
    templates: DeliveryCheckTemplate[],
    scopePredict: Predict,
  ) => {
    // if the template has no available configuration, skip it entirely
    const templatesInScope = templates.filter(
      x => x.conf != null && x.conf.items.length > 0 && scopePredict(x),
    );

    // saves match groups by match kind set
    const matchGroupMap = new Map<string, MatchGroup>();

    for (const template of templatesInScope) {
      const match = matchTemplate(template);

      if (match != null) {
        // populate the key for the target match group by combining filter types
        const key = match.slice(0, MATCH_CARDINALITY).join(':');
        const group = matchGroupMap.get(key) ?? {
          matches: [],
        };

        // the first default template in the same group always takes precedence
        // over others
        if (!group.matches.some(x => templateOfMatch(x).isSystemDefault)) {
          if (template.isSystemDefault) {
            group.matches = [match];
          } else {
            group.matches.push(match);
          }
        }

        matchGroupMap.set(key, group);
      }
    }

    // sort match groups by cardinality of the filter set in descending order
    const matchGroups = [...matchGroupMap.values()].sort((x, y) =>
      cardinalityOfMatchGroup(x) > cardinalityOfMatchGroup(y) ? -1 : 1,
    );

    if (!matchGroups.length) {
      return undefined;
    }

    // select match groups w/ the same cardinality from max to min (0)
    const matches: Match[] = [];

    for (const matchGroup of matchGroups) {
      for (const match of matchGroup.matches) {
        if (
          matches.every(x => matchEquals(x, match) || !isSubMatchOf(x, match))
        ) {
          matches.push(match);
        }
      }
    }

    if (!matches.length) {
      return undefined;
    }

    if (matches.length === 1) {
      return templateOfMatch(matches[0]);
    }

    // if there're multiple selected templates, then populate an artificial
    // delivery check template w/ all items in them combined together.
    const first = templateOfMatch(matches[0]);
    return {
      ...first,
      conf: {
        ...first.conf,
        items: uniqBy(
          x => x.subject,
          matches.flatMap(x => templateOfMatch(x).conf!.items),
        ),
      },
    };
  };

  const matchPartition = (filter: (x: DeliveryCheckTemplate) => boolean) => {
    const l = templateList.filter(filter);
    // match starting from most specific scope to least specific scope
    // that is from store -> org -> agent -> system
    return (
      matchInScope(l, x => x.storeId === storeId && x.orgId === orgId) ??
      matchInScope(l, x => x.orgId === orgId && x.storeId == null) ??
      matchInScope(l, x => agentId != null && x.agentId === agentId) ??
      matchInScope(l, x => !x.orgId && !x.storeId)
    );
  };

  return (
    // templates with fuel type takes precedence over those without
    matchPartition(x => x.fuelType != null) ??
    matchPartition(x => x.fuelType == null)
  );
};
